<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	entityVariant: $.select(
		'Widget Context',
		[
			{ label: 'Homepage', value: '' },
			{ label: 'Book', value: 'book' },
			{ label: 'Holiday', value: 'holiday' },
			{ label: 'Torah Portion', value: 'parashah' },
			{ label: 'Category', value: 'category' },
		],
		{ default: '' },
	),
	defaultTab: $.select(
		'Default Open Tab',
		[
			{ label: 'Latest', value: 'Latest' },
			{ label: 'Popular', value: 'Popular' },
			{ label: 'Featured', value: 'Featured' },
		],
		{ default: 'Latest' },
	),
})
export const BlockWidgets = defineBlock({
	thumbnail: '/block-thumbs/BlockWidgets.svg',
	category: 'Listings',
	order: 300,
	name: 'BlockWidgets',
	label: 'Widgets',
	icon: 'widgets',
	schema,
	dataSource: { collection: 'articles', store: 'the_torah', label: 'Articles' },
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { EmptyState, Icon, Skeleton } from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import ArticleCard from '@/components/blocks/ArticleCard.vue'
import { useBoundPageItem } from '@/composables/useBoundPageItem'
import {
	ensureAuthors,
	resolveAuthorForBlock,
	useArticlesWithAuthors,
	useCollection,
} from '@/composables/useDatastore'
import { useParashaWidget } from '@/composables/useParashaWidget'
import { useWeeklyParasha } from '@/composables/useWeeklyParasha'
import { useParashahSlugs } from '@/composables/useParashahSlugs'



const props = defineProps<{
	entityVariant?: '' | 'topic' | 'book' | 'holiday' | 'parashah' | 'category'
	defaultTab?: 'Latest' | 'Popular' | 'Featured'
	showTorahPortion?: boolean
	loading?: boolean
	latestArticles?: {
		title: string
		authors: { name: string; title: string; image: string }[]
		image: string
		category?: string
		link: string
	}[]
	popularArticles?: {
		title: string
		authors: { name: string; title: string; image: string }[]
		image: string
		category?: string
		link: string
	}[]
	featuredArticles?: {
		title: string
		authors: { name: string; title: string; image: string }[]
		image: string
		category?: string
		link: string
	}[]
}>()

// Tabs
type WidgetTab = 'Latest' | 'Popular' | 'Featured'
const PREVIEW_ARTICLES = [
	{
		title: 'A Sample Torah Article',
		authors: [{ name: 'Sample Author', title: 'Biblical Scholar', image: '/webclip.png' }],
		image: '/og.jpg',
		category: 'Biblical Studies',
		link: '/',
	},
	{
		title: 'Reading Torah in Its Ancient Context',
		authors: [{ name: 'Sample Author', title: 'Professor', image: '/webclip.png' }],
		image: '/og.jpg',
		category: 'Torah Study',
		link: '/',
	},
]
const { isPreview } = useBloxRuntime()
const activeTab = ref<WidgetTab>(props.defaultTab ?? 'Latest')
const tabs: WidgetTab[] = ['Latest', 'Popular', 'Featured']

function setActiveTab(tab: WidgetTab) {
	activeTab.value = tab
}

watch(
	() => props.defaultTab,
	tab => {
		activeTab.value = tab ?? 'Latest'
	},
)

// This week's Torah portion is computed from the Hebrew calendar.
const { primary: weeklyPortion, reference: weeklyReference } = useWeeklyParasha()
const { pathFor: parashahLink } = useParashahSlugs()
const portionName = computed(() => weeklyPortion.value?.name || '')
const portionHebrew = computed(() => weeklyPortion.value?.nameHebrew || '')
const portionReference = computed(() => weeklyReference.value || '')
const portionLink = computed(() =>
	weeklyPortion.value?.name ? parashahLink(weeklyPortion.value.name) : '',
)

const entity = useBoundPageItem<Record<string, any>>()
// The homepage widget is parashah-scoped (see `useParashaWidget`); entity widgets
// (book / holiday / category) keep using the bound page item instead.
const parashaWidget = props.entityVariant ? null : useParashaWidget()
const { articles: datastoreArticles, loading: datastoreLoading } = useArticlesWithAuthors({
	limit: 60,
})
ensureAuthors()

// Map a raw datastore article → the compact shape ArticleCard expects, while
// preserving the fields the tab filters need (hits, publishDate, featuredHomepage).
function toCard(article: any) {
	const authorIds = Array.isArray(article.authorIds)
		? article.authorIds
		: [article.authorId, article.secondAuthorId, article.thirdAuthorId].filter(Boolean)
	const resolvedAuthors = authorIds.map(resolveAuthorForBlock).filter(Boolean)
	return {
		title: article.title || '',
		authors: resolvedAuthors.length ? resolvedAuthors : article.authors || [],
		image: article.image || '',
		category: article.category || '',
		link: article.link || `/article/${article.slug}`,
		publishDate: article.publishDate || '',
		hits: article.hits || 0,
		featuredHomepage: !!article.featuredHomepage,
	}
}

// Which article field tags a piece with the bound entity. `book` is special
// (it collects through `bookArticleIds` + its parashot), so it stays out of this map.
const ENTITY_ARTICLE_FIELD: Partial<Record<'topic' | 'holiday' | 'parashah' | 'category', string>> = {
	topic: 'topicIds',
	holiday: 'holidayIds',
	parashah: 'parshaIds',
	category: 'categoryIds',
}

/**
 * Prerendered entity pages embed their own essays in `_ssgArticles`. Everything
 * else — local dev, or a record resolved straight from the API — has no snapshot,
 * so the pool was empty and the widget rendered nothing at all. Rebuild the pool
 * from the collection instead, with the same rules `build-ssg-snapshot.ts` uses.
 * `clientOnly` keeps the fetch out of the SSG payload and `enabled` skips it
 * wherever the snapshot is present.
 */
const usesLocalPool = () =>
	!!props.entityVariant && !!entity.value?._id && !Array.isArray(entity.value._ssgArticles)

const { items: localArticles, loading: localPoolLoading } = useCollection<any>('articles', {
	limit: 1000,
	all: true,
	clientOnly: true,
	enabled: usesLocalPool,
	pick: [
		'id', '_id', 'slug', 'title', 'image', 'publishDate', 'hits', 'featuredHomepage',
		'authorIds', 'authorId', 'secondAuthorId', 'thirdAuthorId', 'authors',
		'topicIds', 'holidayIds', 'parshaIds', 'categoryIds',
	],
})

const entityArticles = computed(() => {
	if (!props.entityVariant || !entity.value?._id) return datastoreArticles.value
	const embedded = entity.value._ssgArticles
	if (Array.isArray(embedded)) return embedded

	const record = entity.value
	const id = record._id ?? record.id
	const directIds = new Set<string>(props.entityVariant === 'book' ? record.bookArticleIds ?? [] : [])
	const portionIds = new Set<string>(props.entityVariant === 'book' ? record.parashahIds ?? [] : [])
	const field = ENTITY_ARTICLE_FIELD[props.entityVariant as keyof typeof ENTITY_ARTICLE_FIELD]
	return localArticles.value.filter((article: any) => {
		if (directIds.has(article._id) || directIds.has(article.id)) return true
		if (field && Array.isArray(article[field]) && article[field].includes(id)) return true
		return props.entityVariant === 'book'
			&& Array.isArray(article.parshaIds)
			&& article.parshaIds.some((portionId: string) => portionIds.has(portionId))
	})
})

// A record without the build snapshot loads its own pool client-side; keep the
// placeholders up until that request settles so the block doesn't blink away.
const isLoading = computed(
	() => props.loading === true || (props.loading === undefined && datastoreLoading.value),
)

// Curated id lists (`latestArticleIds`, `popularArticleIds`, `featuredArticleIds`)
// point at articles anywhere in the collection, so they must resolve against the
// entity's own pool — not just the 60-article page fetched for the homepage. When
// the lookup only saw that page, any curated id outside it resolved to nothing and
// *every* tab fell back to "newest", so Latest and Featured showed the same pieces.
const articleById = computed(() => {
	const map = new Map<string, any>()
	for (const article of [...entityArticles.value, ...localArticles.value, ...datastoreArticles.value]) {
		const id = article?._id
		if (id && !map.has(id)) map.set(id, article)
	}
	return map
})

function articlesByIds(ids: string[] = []) {
	return ids
		.map(id => articleById.value.get(id))
		.filter((article): article is any => !!article)
}

// Newest first — most articles have a publishDate; undated ones sink to the end.
// Equal dates fall back to the most-read piece so the order is stable (the legacy
// widget sorts that way too, e.g. two essays published on the same day).
function byNewest(a: any, b: any) {
	const byDate = new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime()
	return byDate || (b.hits || 0) - (a.hits || 0)
}

// Most-read first (`hits`); ties fall back to the newest piece.
function byPopular(a: any, b: any) {
	return (b.hits || 0) - (a.hits || 0) || byNewest(a, b)
}

const articlesMap = computed(() => {
	const raw = entityArticles.value
	// Older saved homepage blocks can still carry empty manual arrays. An empty
	// array is truthy, so treating its mere presence as supplied content hides all
	// datastore articles. Only switch to parent-provided content when at least one
	// list actually contains articles.
	const hasParentArticles = [
		props.latestArticles,
		props.popularArticles,
		props.featuredArticles,
	].some(list => (list?.length ?? 0) > 0)
	if (hasParentArticles) {
		return {
			Latest: props.latestArticles ?? [],
			Popular: props.popularArticles ?? [],
			Featured: props.featuredArticles ?? [],
		}
	}

	// Homepage: the tabs describe this week's parashah (latest / most-read /
	// editor-curated for that portion), matching the legacy homepage widget.
	if (!props.entityVariant) {
		const tabs = {
			Latest: parashaWidget?.latest.value ?? [],
			Popular: parashaWidget?.popular.value ?? [],
			Featured: parashaWidget?.featured.value ?? [],
		}
		const preview = isPreview.value && !parashaWidget?.hasArticles.value
		return preview
			? { Latest: PREVIEW_ARTICLES, Popular: PREVIEW_ARTICLES, Featured: PREVIEW_ARTICLES }
			: tabs
	}

	// Entity widgets resolve the three tabs the way the live site does:
	//   Latest   → newest pieces tagged with the entity (`publishDate`)
	//   Popular  → most-read pieces of the entity (`hits`)
	//   Featured → the entity's editor-curated `featuredArticleIds`
	// The records also carry `latestArticleIds`/`popularArticleIds`, but the live
	// widget ignores them — honouring `latestArticleIds` printed months-old
	// "latest" pieces (e.g. 2023 essays on a category page), so they stay unused.
	// `featuredArticleIds` can point at pieces that are not tagged with this entity
	// (e.g. a parashah's hand-picked essays from another portion), so prefer the
	// build-time resolution of that list and only fall back to the local pool.
	const curatedFeatured = entity.value?._ssgFeaturedArticles
	const featuredSelected = Array.isArray(curatedFeatured) && curatedFeatured.length
		? curatedFeatured
		: articlesByIds(entity.value?.featuredArticleIds)

	const latest = [...raw].sort(byNewest).slice(0, 2)
	const popular = [...raw].sort(byPopular).slice(0, 2)

	// Featured: the entity's editor-curated `featuredArticleIds` (e.g. a holiday's
	// hand-picked pieces). Only when the CMS has none do we fall back to flagged
	// pieces, then to the newest articles the Latest tab isn't already showing —
	// otherwise both tabs render the exact same pair.
	const latestIds = new Set(latest.map((article: any) => article._id))
	const flagged = featuredSelected.length
		? featuredSelected
		: raw.filter((a: any) => a.featuredHomepage)
	const featuredFallback = [...raw]
		.sort(byNewest)
		.filter((article: any) => !latestIds.has(article._id))
	const featured = (
		flagged.length ? flagged : featuredFallback.length ? featuredFallback : [...raw].sort(byNewest)
	).slice(0, 2)
	const preview = !isLoading.value && isPreview.value && !raw.length && !hasParentArticles

	return {
		Latest: preview ? PREVIEW_ARTICLES : latest.map(toCard),
		Popular: preview ? PREVIEW_ARTICLES : popular.map(toCard),
		Featured: preview ? PREVIEW_ARTICLES : featured.map(toCard),
	}
})
const activeArticles = computed(
	() => articlesMap.value[activeTab.value as keyof typeof articlesMap.value] ?? [],
)
// An idle hydration state is not a completed fetch. Keep the placeholders
// until articles arrive or an observed request settles (including empty/error).
const articlesRequestSettled = ref(false)
watch(
	isLoading,
	(pending, wasPending) => {
		if (wasPending && !pending) articlesRequestSettled.value = true
	},
	{ flush: 'sync' },
)
const showSkeleton = computed(() => {
	if (activeArticles.value.length) return false
	if (!props.entityVariant) return isLoading.value || parashaWidget?.loading.value === true
	// A record without the build snapshot rebuilds its pool client-side; keep the
	// placeholders up until that lands so the block doesn't blink out and back.
	if (usesLocalPool() && localPoolLoading.value) return true
	return isLoading.value || (props.loading !== false && !articlesRequestSettled.value)
})

// The wrapper must not follow the *active* tab: an empty tab used to hide the
// whole widget — tabs included — so there was no way back to a populated one.
// Show the block whenever any tab has pieces and let the empty tab render a
// placeholder in the grid instead.
const hasAnyArticles = computed(() =>
	(['Latest', 'Popular', 'Featured'] as WidgetTab[]).some(tab => (articlesMap.value[tab]?.length ?? 0) > 0),
)
</script>

<template>
	<div
		v-if="!props.entityVariant || showSkeleton || hasAnyArticles"
		id="entity-articles"
		class="w1100px grid grid-cols-sidebar m_grid-template-columns-1 col-gap-2 mt-2 mb-0 min-h-400px m_px-1"
		:class="{ 'entity-widget': props.entityVariant }"
		:aria-busy="showSkeleton || isLoading"
	>
		<template v-if="showSkeleton">
			<div aria-hidden="true">
				<div v-if="!props.entityVariant && portionName" class="pb-2">
					<Skeleton width="150px" height="28px" class="mb-1" />
					<Skeleton width="110px" height="15px" />
				</div>
				<div class="m_flex m_gap-1">
					<Skeleton
						v-for="tab in 3"
						:key="tab"
						width="90px"
						height="20px"
						class="mb-1-75 m_mb-05"
					/>
				</div>
			</div>
			<div
				class="grid grid-wrap-2 col-gap-1 row-gap-2 align-content-start m_gap-1-5"
				role="status"
				aria-label="Loading articles"
				aria-busy="true"
			>
				<div v-for="card in 2" :key="card" aria-hidden="true">
					<Skeleton width="100%" class="aspect-ratio-3-2" />
					<div class="grid gap-05 pt-05 pb-025">
						<Skeleton width="100%" height="24px" />
						<Skeleton width="75%" height="24px" />
					</div>
					<Skeleton width="55%" height="15px" />
				</div>
			</div>
		</template>
		<template v-else>
			<div>
				<div v-if="!props.entityVariant && portionName && portionLink" class="pb-2">
					<RouterLink :to="portionLink" class="decoration-none block redhover">
						<h3 class="txt24 my-0 regular line-height-13 m_inline-block pe-05">
							{{ portionName }}
						</h3>
						<h3 class="txt27 my-0 regular line-height-13 m_inline-block">
							{{ portionHebrew }}
						</h3>
					</RouterLink>
					<RouterLink
						:to="portionLink"
						class="txt12 color-gray-70 flex gap-025 pt-075 decoration-none"
					>
						<i class="pretty">{{ portionReference }}</i>
						<Icon name="keyboard_arrow_right" class="color-primary line-height-0" />
					</RouterLink>
				</div>

				<div class="m_flex m_gap-1">
					<div
						v-for="tab in tabs"
						:key="tab"
						class="txt17 m_txt15 hover mb-1-75 m_mb-05 widgetTab flex gap-1"
						:class="{ active: activeTab === tab }"
						@click="setActiveTab(tab)"
					>
						{{ tab }}
					</div>
				</div>
			</div>

			<!-- Articles live in their own 2-column grid so they never spill into the
		     sidebar column and every row's images/titles align cleanly. -->
			<div class="grid grid-wrap-2 col-gap-1 row-gap-2 align-content-start m_gap-1-5 txt-start">
				<ArticleCard
					v-for="article in activeArticles"
					:key="article?.title"
					:article="article!"
					widget
				/>
				<EmptyState
					v-if="!activeArticles.length"
					icon="none"
					:title="`No ${activeTab.toLowerCase()} pieces yet`"
					class="grid-span-2 m_grid-span-1"
				/>
			</div>
		</template>
	</div>
</template>

<style scoped>
.grid-cols-sidebar {
	grid-template-columns: 200px 1fr;
}
.widgetTab:hover,
.widgetTab.active {
	color: var(--bgl-primary);
}
.widgetTab::before {
	content: '';
	display: block;
	width: 0;
	height: 2px;
	background: var(--bgl-primary);
	transition: var(--bgl-transition);
}

.widgetTab.active::before {
	width: 30px;
}
@media (max-width: 910px) {
	.widgetTab::before {
		display: none;
	}
}
</style>
